import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditUserComposer } from "@/forms/users"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { UserCog, Loader2 } from "lucide-react"
import { UserStatus, type Invitation } from "@/types/users"
import { useToast } from "@/contexts/ToastContext"
import { findUserInvitation, invitationDisplayStatus, isInvitationActionable } from "@/lib/invitations"

function EditUserPage() {
  const { userNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useApiQuery('getUserByNumber', { userNumber }, {})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const user = (data as any)?.content?.user

  // Invitation data isn't part of the user record (getUser has no invitation fields).
  // Fetch the invitation list and match this user (acceptedUserNumber, else email) to get the
  // invitation's status and invitationNumber — both needed for the Resend Invitation flow.
  const { data: invitationData } = useApiQuery('getInvitationList', {}, {})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const invitations: Invitation[] = (invitationData as any)?.content?.invitations ?? []
  const userInvitation = user ? findUserInvitation(invitations, user) : undefined

  const handleBack = () => navigate({ to: '/users' })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Derive IDs/status the same way the user list does
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const organizationIds: string[] = (user?.organizations || []).map((org: any) =>
    typeof org === 'string' ? org : (org.orgNumber?.toString() || org.id)
  ).filter(Boolean)

  const userStatus = (String(user?.userStatus || 'active').toLowerCase() as UserStatus)
  const invitationStatus = userInvitation ? invitationDisplayStatus(userInvitation) : undefined
  // Resend only works while the backend still has the invitation as `pending`.
  const invitationNumber = userInvitation && isInvitationActionable(userInvitation)
    ? Number(userInvitation.invitationNumber)
    : undefined
  const phone = user?.countryCode && user?.mobileNumber
    ? `+${user.countryCode}${user.mobileNumber}`
    : undefined
  // Tenants the user belongs to; backend requires at least one (default to conex).
  const tenants: ('conex' | 'farmgrid')[] =
    Array.isArray(user?.tenants) && user.tenants.length > 0
      ? user.tenants.filter((t: unknown): t is 'conex' | 'farmgrid' => t === 'conex' || t === 'farmgrid')
      : ['conex']

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <UserCog className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">User</CardTitle>
              <CardDescription className="text-base">Update user details</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {user ? (
            <EditUserComposer
              // Data-versioned key: re-mount when a fresh copy arrives so the form
              // re-initializes instead of showing previously-cached values.
              key={`user-${user.userNumber}-${user.updatedAt ?? ''}`}
              userNumber={user.userNumber?.toString() ?? ''}
              currentUserStatus={userStatus}
              currentInvitationStatus={invitationStatus}
              invitationNumber={invitationNumber}
              initialValues={{
                firstName: user.firstName,
                lastName: user.lastName,
                emailAddress: user.emailAddress ?? '',
                phone,
                organizations: organizationIds,
                tenants,
                profilePicture: user.profilePicture || user.profilePictureLocation,
                userNumber: user.userNumber,
              }}
              submitLabel="Save User"
              showHeader={false}
              onSuccess={handleBack}
              onError={(error) => toast({ title: 'Error', description: `Failed to update user: ${error.message}`, type: 'error' })}
              onCancel={handleBack}
            />
          ) : (
            <p className="text-sm text-muted-foreground">User not found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/users/$userNumber_/edit")({
  staticData: {
    title: 'Edit User',
    isSecured: true,
  },
  component: EditUserPage,
})
